/**
 * Reads Cloudinary-related env vars (supports REACT_APP_CLOUDINARY_* and legacy REACT_APP_CLOUDNARY_* typos).
 */
function firstEnv(...keys) {
  for (const key of keys) {
    const v = process.env[key];
    if (v != null && v !== "") return v;
  }
  return "";
}

/**
 * True when REACT_APP_CLOUDINARY_URL already points at a cloud folder, e.g.
 * https://res.cloudinary.com/dpki3q2cc/
 */
function deliveryUrlIncludesCloudFolder(url) {
  if (!url || !url.includes("res.cloudinary.com")) return false;
  try {
    const u = new URL(url);
    const parts = u.pathname.replace(/\/$/, "").split("/").filter(Boolean);
    return parts.length >= 1;
  } catch {
    return false;
  }
}

/**
 * Base URL for delivery assets (project images, dummy-profile.png, etc.).
 * New .env: REACT_APP_CLOUDINARY_URL + REACT_APP_CLOUDINARY_SUBFOLDER
 * Legacy: URL + NAME + SUBFLODER (name was part of the path)
 */
export function getCloudinaryMediaBaseUrl() {
  const url = firstEnv(
    "REACT_APP_CLOUDINARY_URL",
    "REACT_APP_CLOUDNARY_URL"
  );
  const name = firstEnv(
    "REACT_APP_CLOUDINARY_NAME",
    "REACT_APP_CLOUDNARY_NAME"
  );
  const sub = firstEnv(
    "REACT_APP_CLOUDINARY_SUBFOLDER",
    "REACT_APP_CLOUDNARY_SUBFLODER"
  );
  const subPart = sub.replace(/^\//, "");
  const base = url ? (url.endsWith("/") ? url : `${url}/`) : "";

  if (url && subPart && deliveryUrlIncludesCloudFolder(url)) {
    return `${base}${subPart}`;
  }
  return `${url}${name}${sub}`;
}

/**
 * Root for building paths: https://res.cloudinary.com/<cloud>/
 */
export function getCloudinaryDeliveryRoot() {
  const url = firstEnv(
    "REACT_APP_CLOUDINARY_URL",
    "REACT_APP_CLOUDNARY_URL"
  );
  const name = firstEnv(
    "REACT_APP_CLOUDINARY_NAME",
    "REACT_APP_CLOUDNARY_NAME"
  );
  if (!url) return "";
  const base = url.endsWith("/") ? url : `${url}/`;
  if (deliveryUrlIncludesCloudFolder(url)) return base;
  return `${base}${name}/`.replace(/([^:]\/)\/+/g, "$1");
}

/**
 * Base for static / empty-state images (NODATAIMAGE_URL, etc.).
 */
export function getCloudinaryStaticBaseUrl() {
  let staticSeg = firstEnv(
    "REACT_APP_CLOUDINARY_STATIC_IMAGES",
    "REACT_APP_CLOUDNARY_STATIC_IMAGES"
  );
  if (!staticSeg) {
    const sub = firstEnv(
      "REACT_APP_CLOUDINARY_SUBFOLDER",
      "REACT_APP_CLOUDNARY_SUBFLODER"
    );
    if (sub) {
      staticSeg = `${sub.replace(/\/$/, "")}/Static-Images/`;
    }
  }
  const root = getCloudinaryDeliveryRoot();
  if (!staticSeg) return root;
  return `${root}${staticSeg.replace(/^\//, "")}`;
}

export function getCloudinaryCloudName() {
  return firstEnv(
    "REACT_APP_CLOUDINARY_NAME",
    "REACT_APP_CLOUDNARY_NAME"
  );
}

export function getCloudinaryUploadPreset() {
  return (
    firstEnv("REACT_APP_CLOUDINARY_UPLOAD_PRESET") ||
    "RizzTix"
  );
}

/** Optional mobile uploads folder; falls back to project media base. */
export function getCloudinaryMobileImageBaseUrl() {
  const mobile = firstEnv(
    "REACT_APP_CLOUDINARY_MOBILE_IMAGE_FOLDER",
    "REACT_APP_CLOUDNARY_MOBILE_IMAGE_FOLDER"
  );
  if (mobile) {
    return `${getCloudinaryDeliveryRoot()}${mobile.replace(/^\//, "")}`;
  }
  return getCloudinaryMediaBaseUrl();
}

/** Full mobile delivery base when REACT_APP_CLOUDINARY_URL_MOBILE is set (legacy). */
export function getCloudinaryMobileDeliveryBase() {
  const full = firstEnv("REACT_APP_CLOUDINARY_URL_MOBILE");
  if (full) return full.endsWith("/") ? full : `${full}/`;
  return getCloudinaryMobileImageBaseUrl();
}
